import React, { createContext, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';
import useLwtReducer, {
  ResolverType,
} from '@lwtears/lwt-common-frontend/lib/@common/hooks/use-lwt-reducer';
import { TelemetryEvents } from '../../types';
import { nowISOFormat } from '../../util/date-util';

export const TelemetryContext = createContext<any>([]);

export const defaultState = (): {
  data: { [telemetryEvent: string]: { [key: string]: any } };
} => ({ data: {} });

const resolverMap = {
  SET_DATA: (state, { payload }) => ({
    ...state,
    data: {
      ...state.data,
      [payload.event]: payload.clearPreviousData
        ? payload.data
        : { ...state.data[payload.event], ...payload.data },
    },
  }),
  CLEAR_DATA: (state, { payload }) => ({
    ...state,
    data: {
      ...state.data,
      [payload.event]: {},
    },
  }),
};

export const BaseTelemetryProvider: React.FC<{
  telemetryEventsMutationMap: { [key: string]: DocumentNode };
}> = ({ children, telemetryEventsMutationMap }) => {
  const [state, dispatch] = useLwtReducer<
    ResolverType<typeof resolverMap>,
    ReturnType<typeof defaultState>
  >(resolverMap, defaultState());
  const client = useApolloClient();

  const setTelemetryEventData = useCallback(
    (event: TelemetryEvents) => (data: any, clearPreviousData?: boolean) => {
      dispatch({ type: 'SET_DATA', payload: { event, data, clearPreviousData } });
    },
    []
  );
  const clearTelemetryEventData = useCallback(
    (event: TelemetryEvents) => () => {
      dispatch({ type: 'CLEAR_DATA', payload: { event } });
    },
    []
  );

  const fireEvent = useCallback(
    (event: TelemetryEvents) =>
      async (data?: { [key: string]: any }, clearPreviousData?: boolean) => {
        let eventData = {};
        if (data) {
          eventData = clearPreviousData ? data : { ...state.data[event], ...data };
          setTelemetryEventData(event)(data);
        } else {
          eventData = state.data[event];
        }
        try {
          const mutation = telemetryEventsMutationMap[event];
          if (!mutation) {
            console.warn(`No telemetry mutation mapped for event: ${event}`);
            return;
          }
          await client.mutate({
            mutation,
            variables: {
              data: { ...eventData, timestamp: nowISOFormat() },
            },
          });
        } catch (e) {
          console.error(e);
        }
      },
    [state, client, telemetryEventsMutationMap]
  );

  return (
    <TelemetryContext.Provider
      value={[
        {
          ...state,
        },
        { setTelemetryEventData, clearTelemetryEventData, fireEvent },
      ]}
    >
      {children}
    </TelemetryContext.Provider>
  );
};

export default BaseTelemetryProvider;
